import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { maplibregl } from '@/lib/map/maplibre'
import { useTheme } from '@/lib/theme'
import { useBasemap } from '@/lib/basemap'
import { activeBasemapProvider, transformCartoRequest } from '@/lib/map'
import { normalizeCoord } from '@/lib/geo'
import type { LocationHit } from '@/lib/api/location'

export interface MapHandle {
  /**
   * Animate the map to the supplied `[lon, lat]`. By default the pin
   * follows the camera (Search + My-Location behavior). Pass
   * `{ moveMarker: false }` to keep the pin where it is — used when
   * the user clicks a result row to inspect a specific hit without
   * relocating their original input coordinate.
   */
  flyTo: (
    lonLat: [number, number],
    zoom?: number,
    opts?: { moveMarker?: boolean },
  ) => void
  /**
   * Animate the map to fit the given GeoJSON bounding box, with padding
   * tuned for the floating chrome. Used by Search to fit the smallest
   * containing geography after a lookup.
   */
  fitBounds: (bounds: [number, number, number, number]) => void
}

interface MapProps {
  center: [number, number]
  zoom?: number
  /** Fires throughout a drag (rAF-throttled). Use to keep coord inputs in sync. */
  onCoordChange?: (lonLat: [number, number]) => void
  /** Fires once when the user releases the pin OR clicks the map. */
  onCoordCommit?: (lonLat: [number, number]) => void
  /** Fires (debounced to zoomend) with the map's zoom, for URL state. */
  onZoomChange?: (zoom: number) => void
  /**
   * Boundaries to outline on the map. Each hit's `display_geom` is drawn as
   * a green stroke whose width varies by layer importance + whether the
   * matching `geoborder_id` is currently hovered.
   */
  hits?: LocationHit[]
  /**
   * If non-null, the corresponding boundary outline gets bumped (wider +
   * brighter). Driven by ResultsPanel row hover.
   */
  hoveredGeoborderId?: string | null
  /**
   * When true, overlay the survey-grade U.S. land border (IBWC + IBC) as a
   * bright line. Lazily fetched from /geo/us-survey-border.geojson on first
   * enable. Used by the Explore page's toggle.
   */
  showSurveyBorder?: boolean
}

const HITS_SOURCE_ID = 'gb-hits'
const HITS_LAYER_ID = 'gb-hits-outline'
const HITS_FILL_LAYER_ID = 'gb-hits-fill'
// Separate layer for Point-geometry hits (immigration courts, ICE
// field offices). MapLibre's `fill` + `line` layers silently skip
// Point features; we render them as small circle markers here.
const HITS_POINT_LAYER_ID = 'gb-hits-point'
// Halo behind the hovered point — same visual cue as the polygon
// fill-on-hover, just expressed as a translucent disc instead of a
// flooded shape. Sits below HITS_POINT_LAYER_ID so the solid dot
// reads on top.
const HITS_POINT_HALO_LAYER_ID = 'gb-hits-point-halo'

// Survey-grade U.S. land-border overlay (IBWC + IBC), toggled on Explore.
const SURVEY_BORDER_SOURCE_ID = 'gb-survey-border'
const SURVEY_BORDER_CASING_LAYER_ID = 'gb-survey-border-casing'
const SURVEY_BORDER_LAYER_ID = 'gb-survey-border-line'
const SURVEY_BORDER_URL = '/geo/us-survey-border.geojson'

/**
 * Add (once) or toggle the survey-border source + layers. A dark casing under
 * a bright mint line keeps the border legible over both light and dark
 * basemaps. No-op until the GeoJSON has been fetched into `data`.
 */
function ensureSurveyBorderLayer(
  map: maplibregl.Map,
  data: GeoJSON.FeatureCollection | null,
  visible: boolean,
) {
  const vis = visible ? 'visible' : 'none'
  if (map.getSource(SURVEY_BORDER_SOURCE_ID)) {
    if (map.getLayer(SURVEY_BORDER_CASING_LAYER_ID))
      map.setLayoutProperty(SURVEY_BORDER_CASING_LAYER_ID, 'visibility', vis)
    if (map.getLayer(SURVEY_BORDER_LAYER_ID))
      map.setLayoutProperty(SURVEY_BORDER_LAYER_ID, 'visibility', vis)
    return
  }
  if (!data) return
  map.addSource(SURVEY_BORDER_SOURCE_ID, { type: 'geojson', data })
  map.addLayer({
    id: SURVEY_BORDER_CASING_LAYER_ID,
    type: 'line',
    source: SURVEY_BORDER_SOURCE_ID,
    layout: { visibility: vis, 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#0a1a3f', 'line-width': 5.4, 'line-opacity': 0.55, 'line-blur': 0.4 },
  })
  map.addLayer({
    id: SURVEY_BORDER_LAYER_ID,
    type: 'line',
    source: SURVEY_BORDER_SOURCE_ID,
    layout: { visibility: vis, 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#2f7dff', 'line-width': 3.4 },
  })
  raiseSurveyBorder(map)
}

/** Keep the survey border above every other overlay (hit outlines/fills). */
function raiseSurveyBorder(map: maplibregl.Map) {
  if (map.getLayer(SURVEY_BORDER_CASING_LAYER_ID)) map.moveLayer(SURVEY_BORDER_CASING_LAYER_ID)
  if (map.getLayer(SURVEY_BORDER_LAYER_ID)) map.moveLayer(SURVEY_BORDER_LAYER_ID)
}

function buildPinElement(): HTMLElement {
  const root = document.createElement('div')
  root.style.position = 'relative'
  root.style.width = '18px'
  root.style.height = '18px'
  root.style.borderRadius = '50%'
  root.style.background = 'var(--color-signal)'
  root.style.boxShadow = '0 0 0 4px var(--color-signal-soft)'
  // 'pointer' (finger) is visually distinct from the map's open-hand pan
  // cursor — signals "this is interactive". Swaps to 'grabbing' (closed
  // fist) during an active drag for clear feedback through the gesture.
  root.style.cursor = 'pointer'
  root.setAttribute('aria-label', 'Coordinate pin (drag to move)')
  root.setAttribute('role', 'button')
  root.setAttribute('tabindex', '0')

  const ring1 = document.createElement('span')
  ring1.style.position = 'absolute'
  ring1.style.inset = '-10px'
  ring1.style.borderRadius = '50%'
  ring1.style.border = '1px solid var(--color-signal)'
  ring1.style.opacity = '0.4'
  ring1.style.pointerEvents = 'none'
  root.appendChild(ring1)

  const ring2 = document.createElement('span')
  ring2.style.position = 'absolute'
  ring2.style.inset = '-22px'
  ring2.style.borderRadius = '50%'
  ring2.style.border = '1px solid var(--color-signal)'
  ring2.style.opacity = '0.2'
  ring2.style.pointerEvents = 'none'
  root.appendChild(ring2)

  return root
}

function hitsToFeatureCollection(hits: LocationHit[] | undefined): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const hit of hits ?? []) {
    if (!hit.display_geom) continue
    features.push({
      type: 'Feature',
      // The `id` field powers MapLibre's feature-state for hover styling
      // — must be on the Feature, not just in properties.
      id: hit.geoborder_id,
      geometry: hit.display_geom as GeoJSON.Geometry,
      properties: {
        layer_id: hit.layer_id,
        name: hit.name,
        geoborder_id: hit.geoborder_id,
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

// Width by layer importance — country thickest, ZCTA thinnest.
const BASE_WIDTH_EXPR: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'layer_id'], 'country'], 3.0,
  ['==', ['get', 'layer_id'], 'state'], 2.2,
  ['==', ['get', 'layer_id'], 'county'], 1.6,
  ['==', ['get', 'layer_id'], 'cbsa'], 1.4,
  ['==', ['get', 'layer_id'], 'congressional_district'], 1.4,
  ['==', ['get', 'layer_id'], 'zcta'], 1.0,
  1.2,
]

/**
 * Build the paint expressions used by the outline + fill layers. Driven by
 * the geoborder_id property on each feature (a data-driven approach instead
 * of feature-state) — that way the hover styling survives style reloads
 * (theme switch, basemap kind change) without us having to re-apply state.
 *
 * `hoveredId` is the geoborder_id currently being hovered in the result
 * panel, or null when nothing is hovered.
 */
function buildPaintExpressions(hoveredId: string | null): {
  fillOpacity: maplibregl.ExpressionSpecification | number
  lineOpacity: maplibregl.ExpressionSpecification | number
  lineWidth: maplibregl.ExpressionSpecification
} {
  if (hoveredId == null) {
    return {
      fillOpacity: 0,
      lineOpacity: 0.85,
      lineWidth: BASE_WIDTH_EXPR,
    }
  }
  const matchHovered: maplibregl.ExpressionSpecification = [
    '==',
    ['get', 'geoborder_id'],
    hoveredId,
  ]
  return {
    // 0.35 against the cream basemap is the sweet spot — 0.22 was too
    // subtle, 0.5+ swamps the underlying labels.
    fillOpacity: ['case', matchHovered, 0.35, 0],
    lineOpacity: ['case', matchHovered, 1.0, 0.85],
    // Bump the outline by 1.5px on the hovered polygon for an extra cue
    // alongside the fill.
    lineWidth: ['+', BASE_WIDTH_EXPR, ['case', matchHovered, 1.5, 0]],
  }
}

// Point-layer paint. Points are immigration courts / ICE field
// offices: rendered as small circles, bumped up + recolored on hover.
function buildPointPaint(hoveredId: string | null): {
  pointRadius: maplibregl.ExpressionSpecification | number
  pointStrokeWidth: maplibregl.ExpressionSpecification | number
  pointColor: maplibregl.ExpressionSpecification | string
  haloOpacity: maplibregl.ExpressionSpecification | number
} {
  if (hoveredId == null) {
    return {
      pointRadius: 5,
      pointStrokeWidth: 1.5,
      pointColor: '#1FA968',
      haloOpacity: 0,
    }
  }
  const matchHovered: maplibregl.ExpressionSpecification = [
    '==',
    ['get', 'geoborder_id'],
    hoveredId,
  ]
  return {
    // Bigger dot + thicker stroke + lighter (mint) fill on the hovered
    // point. Same green-family as the polygon hover highlight.
    pointRadius: ['case', matchHovered, 8, 5],
    pointStrokeWidth: ['case', matchHovered, 2.5, 1.5],
    pointColor: ['case', matchHovered, '#5BD49A', '#1FA968'],
    haloOpacity: ['case', matchHovered, 0.35, 0],
  }
}

function ensureHitsLayer(
  map: maplibregl.Map,
  fc: GeoJSON.FeatureCollection,
  hoveredId: string | null,
): void {
  const { fillOpacity, lineOpacity, lineWidth } = buildPaintExpressions(hoveredId)

  const existingSource = map.getSource(HITS_SOURCE_ID) as
    | maplibregl.GeoJSONSource
    | undefined
  if (existingSource) {
    existingSource.setData(fc)
    // Re-apply current hover state to the layers — the source may have
    // returned different feature ids, and the layers might have been
    // recreated by setStyle.
    if (map.getLayer(HITS_FILL_LAYER_ID)) {
      map.setPaintProperty(HITS_FILL_LAYER_ID, 'fill-opacity', fillOpacity)
    }
    if (map.getLayer(HITS_LAYER_ID)) {
      map.setPaintProperty(HITS_LAYER_ID, 'line-opacity', lineOpacity)
      map.setPaintProperty(HITS_LAYER_ID, 'line-width', lineWidth)
    }
    if (map.getLayer(HITS_POINT_LAYER_ID)) {
      const { pointRadius, pointStrokeWidth, pointColor, haloOpacity } =
        buildPointPaint(hoveredId)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-radius', pointRadius)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-stroke-width', pointStrokeWidth)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-color', pointColor)
      if (map.getLayer(HITS_POINT_HALO_LAYER_ID)) {
        map.setPaintProperty(HITS_POINT_HALO_LAYER_ID, 'circle-opacity', haloOpacity)
      }
    }
    return
  }
  map.addSource(HITS_SOURCE_ID, { type: 'geojson', data: fc })

  // Point hits get their own circle layer (+ a translucent halo
  // layer beneath it that lights up on hover). Polygons + lines
  // silently ignore Point features so this is additive — no need
  // to split the source.
  const { pointRadius, pointStrokeWidth, pointColor, haloOpacity } =
    buildPointPaint(hoveredId)
  map.addLayer({
    id: HITS_POINT_HALO_LAYER_ID,
    type: 'circle',
    source: HITS_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-color': '#1FA968',
      'circle-radius': 16,
      'circle-opacity': haloOpacity,
      'circle-stroke-width': 0,
    },
  })
  map.addLayer({
    id: HITS_POINT_LAYER_ID,
    type: 'circle',
    source: HITS_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-color': pointColor,
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-width': pointStrokeWidth,
      'circle-radius': pointRadius,
      'circle-opacity': 0.95,
    },
  })

  // Add the outline layer first so we can pin the fill BELOW it via the
  // `beforeId` parameter on the fill add — guarantees the outline stays on
  // top regardless of map style restoration order.
  map.addLayer({
    id: HITS_LAYER_ID,
    type: 'line',
    source: HITS_SOURCE_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#1FA968',
      'line-opacity': lineOpacity,
      'line-width': lineWidth,
    },
  })

  // Hover-fill: invisible by default, transparent green on the hovered
  // polygon. Painted BELOW the outline (the `beforeId` arg) so the green
  // border still reads on top.
  map.addLayer(
    {
      id: HITS_FILL_LAYER_ID,
      type: 'fill',
      source: HITS_SOURCE_ID,
      paint: {
        'fill-color': '#1FA968',
        'fill-opacity': fillOpacity,
      },
    },
    HITS_LAYER_ID,
  )
}

export const Map = forwardRef<MapHandle, MapProps>(function Map(
  { center, zoom = 10, onCoordChange, onCoordCommit, onZoomChange, hits, hoveredGeoborderId, showSurveyBorder },
  ref,
) {
  const { theme } = useTheme()
  const { kind } = useBasemap()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const onCoordChangeRef = useRef(onCoordChange)
  const onCoordCommitRef = useRef(onCoordCommit)
  const onZoomChangeRef = useRef(onZoomChange)
  const hitsRef = useRef(hits)
  // Survey-border overlay: cached GeoJSON (fetched once) + latest visibility,
  // both read inside style.load so the overlay survives basemap/theme reloads.
  const surveyBorderDataRef = useRef<GeoJSON.FeatureCollection | null>(null)
  const showSurveyBorderRef = useRef<boolean>(!!showSurveyBorder)
  // The map is created with the correct initial style, so the theme/kind effect
  // must not re-setStyle on mount (that fires before the first load finishes →
  // "style is not done loading" + a needless full rebuild). Skip its first run.
  const styleInitDoneRef = useRef(false)
  // Latest hovered id — read inside style.load callbacks so the hover
  // survives basemap / theme reloads.
  const hoveredIdRef = useRef<string | null>(hoveredGeoborderId ?? null)

  useEffect(() => {
    onCoordChangeRef.current = onCoordChange
  }, [onCoordChange])
  useEffect(() => {
    onCoordCommitRef.current = onCoordCommit
  }, [onCoordCommit])
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange
  }, [onZoomChange])
  useEffect(() => {
    hitsRef.current = hits
  }, [hits])

  useImperativeHandle(
    ref,
    () => ({
      flyTo: (lonLat, z, opts) => {
        const map = mapRef.current
        const marker = markerRef.current
        if (!map || !marker) return
        map.flyTo({
          center: lonLat,
          zoom: z ?? map.getZoom(),
          essential: true,
          duration: 900,
        })
        // Default: marker follows the camera (Search + My-Location).
        // Pass moveMarker: false to fly without relocating the pin —
        // used by result-row clicks where the user just wants to
        // inspect a hit without losing their original coordinate.
        if (opts?.moveMarker !== false) marker.setLngLat(lonLat)
      },
      fitBounds: (bounds) => {
        const map = mapRef.current
        if (!map) return
        map.fitBounds(bounds, {
          // Padding accounts for the floating chrome: top for coord input,
          // right for results panel, bottom for legend/hint pills.
          padding: { top: 110, bottom: 90, left: 40, right: 360 },
          duration: 900,
          maxZoom: 11,
        })
      },
    }),
    [],
  )

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const { style, maxZoom } = activeBasemapProvider.getStyle({ theme, kind })
    const map = new maplibregl.Map({
      container: containerRef.current,
      style,
      center,
      zoom,
      maxZoom,
      attributionControl: false,
      cooperativeGestures: false,
      // Attaches the CARTO key (when one is configured) to the style, tiles,
      // sprites and glyphs alike. No-op for every non-CARTO request.
      transformRequest: transformCartoRequest,
    })

    const marker = new maplibregl.Marker({
      element: buildPinElement(),
      draggable: true,
    })
      .setLngLat(center)
      .addTo(map)

    let rafId: number | null = null
    const emitDrag = () => {
      rafId = null
      const cb = onCoordChangeRef.current
      if (!cb) return
      const { lng, lat } = marker.getLngLat()
      // Wrap across the antimeridian so coords always stay in
      // (-180, 180] / [-90, 90]. MapLibre lets the marker slide past
      // -180 / +180 when the user pans across the antimeridian.
      cb(normalizeCoord(lng, lat))
    }
    const onDrag = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(emitDrag)
    }
    const onDragStart = () => {
      marker.getElement().style.cursor = 'grabbing'
    }
    const onDragEnd = () => {
      marker.getElement().style.cursor = 'pointer'
      emitDrag()
      const cb = onCoordCommitRef.current
      if (!cb) return
      const { lng, lat } = marker.getLngLat()
      cb(normalizeCoord(lng, lat))
    }
    marker.on('dragstart', onDragStart)
    marker.on('drag', onDrag)
    marker.on('dragend', onDragEnd)

    // Click anywhere on the map to relocate the pin AND recenter the map
    // on that point. Touch-friendly: tap works the same way. MapLibre
    // fires `click` after a discrete press + release without movement
    // — drag-to-pan doesn't trigger this. The pin's own click is
    // consumed by MapLibre's marker handling before it reaches the
    // map, so clicks on the pin don't fire this handler.
    //
    // easeTo (not flyTo) keeps the camera at its current zoom — flyTo
    // would zoom out and back in, which is jarring for "I clicked
    // there, just go there".
    const onMapClick = (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat
      // Wrap before doing anything else. The marker, the camera, and the
      // commit callback all need the canonical [-180, 180] representation
      // — otherwise the API rejects the click as out-of-range when the
      // user pans across the antimeridian.
      const [wLng, wLat] = normalizeCoord(lng, lat)
      marker.setLngLat([wLng, wLat])
      map.easeTo({ center: [wLng, wLat], duration: 600 })
      const cb = onCoordCommitRef.current
      if (cb) cb([wLng, wLat])
    }
    map.on('click', onMapClick)

    const reattachHitsLayer = () => {
      ensureHitsLayer(map, hitsToFeatureCollection(hitsRef.current), hoveredIdRef.current)
      ensureSurveyBorderLayer(map, surveyBorderDataRef.current, showSurveyBorderRef.current)
    }
    map.on('style.load', reattachHitsLayer)

    // Report zoom (settled) for URL state — replaceState only, no re-render.
    const onZoomEnd = () => onZoomChangeRef.current?.(map.getZoom())
    map.on('zoomend', onZoomEnd)

    const resizeObserver = new ResizeObserver(() => {
      map.resize()
    })
    resizeObserver.observe(containerRef.current)

    const rafFirstResize = requestAnimationFrame(() => {
      map.resize()
    })

    let fontsResize: number | null = null
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready
        .then(() => {
          fontsResize = window.setTimeout(() => map.resize(), 0)
        })
        .catch(() => {
          /* ignore */
        })
    }

    mapRef.current = map
    markerRef.current = marker

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      cancelAnimationFrame(rafFirstResize)
      if (fontsResize !== null) clearTimeout(fontsResize)
      resizeObserver.disconnect()
      map.off('style.load', reattachHitsLayer)
      map.off('zoomend', onZoomEnd)
      map.off('click', onMapClick)
      marker.off('dragstart', onDragStart)
      marker.off('drag', onDrag)
      marker.off('dragend', onDragEnd)
      marker.remove()
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Push new hits into the source when result changes.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const fc = hitsToFeatureCollection(hits)
    if (map.isStyleLoaded()) {
      ensureHitsLayer(map, fc, hoveredIdRef.current)
      // First result adds hit layers on top; keep the survey border above them.
      raiseSurveyBorder(map)
    }
  }, [hits])

  // Survey-border overlay: fetch the GeoJSON on first enable (cached
  // thereafter), then add/toggle the layer.
  useEffect(() => {
    showSurveyBorderRef.current = !!showSurveyBorder
    const map = mapRef.current
    if (!map) return
    let cancelled = false
    const apply = () => {
      if (cancelled) return
      ensureSurveyBorderLayer(map, surveyBorderDataRef.current, !!showSurveyBorder)
    }
    // Add now if the style is ready, otherwise wait for the first idle — on a
    // fresh page load the fetch can resolve before the initial style finishes,
    // and `style.load` (which reattaches layers) only fires on setStyle, not
    // reliably on first paint. `idle` fires once the initial load settles, so
    // the overlay appears without needing a basemap/theme toggle.
    const applyWhenReady = () => {
      if (cancelled) return
      if (map.isStyleLoaded()) apply()
      else map.once('idle', apply)
    }
    if (showSurveyBorder && !surveyBorderDataRef.current) {
      fetch(SURVEY_BORDER_URL)
        .then((r) => r.json())
        .then((d: GeoJSON.FeatureCollection) => {
          if (cancelled) return
          surveyBorderDataRef.current = d
          applyWhenReady()
        })
        .catch(() => {
          /* overlay is best-effort; ignore fetch/parse errors */
        })
    } else {
      applyWhenReady()
    }
    return () => {
      cancelled = true
    }
  }, [showSurveyBorder])

  // Sync hovered id by reassigning the paint expressions. Data-driven
  // styling (not feature-state) so it works the moment the source is
  // available and survives setStyle reloads via the style.load handler.
  useEffect(() => {
    hoveredIdRef.current = hoveredGeoborderId ?? null
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return
    if (!map.getLayer(HITS_FILL_LAYER_ID)) return
    const { fillOpacity, lineOpacity, lineWidth } = buildPaintExpressions(
      hoveredGeoborderId ?? null,
    )
    map.setPaintProperty(HITS_FILL_LAYER_ID, 'fill-opacity', fillOpacity)
    map.setPaintProperty(HITS_LAYER_ID, 'line-opacity', lineOpacity)
    map.setPaintProperty(HITS_LAYER_ID, 'line-width', lineWidth)
    // Point + halo paint also needs to refresh on hover changes; the
    // ensureHitsLayer fast-path only runs when the source data
    // changes, so without this the dot stays the default green even
    // when the matching row is hovered.
    if (map.getLayer(HITS_POINT_LAYER_ID)) {
      const { pointRadius, pointStrokeWidth, pointColor, haloOpacity } =
        buildPointPaint(hoveredGeoborderId ?? null)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-radius', pointRadius)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-stroke-width', pointStrokeWidth)
      map.setPaintProperty(HITS_POINT_LAYER_ID, 'circle-color', pointColor)
      if (map.getLayer(HITS_POINT_HALO_LAYER_ID)) {
        map.setPaintProperty(HITS_POINT_HALO_LAYER_ID, 'circle-opacity', haloOpacity)
      }
    }
  }, [hoveredGeoborderId])

  // Swap the map style when the theme or basemap kind changes.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Skip the mount run — the map was just created with this exact style.
    if (!styleInitDoneRef.current) {
      styleInitDoneRef.current = true
      return
    }
    const { style, maxZoom } = activeBasemapProvider.getStyle({ theme, kind })
    map.setStyle(style)
    // Cap zoom to the new basemap's imagery (clamps the current view if it's
    // deeper than the new source supports — e.g. street→satellite).
    map.setMaxZoom(maxZoom)
  }, [theme, kind])

  return (
    <div className="absolute inset-0 z-0">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  )
})
